import * as R from 'remeda';
import {
  SchemaDatapointButton,
  SchemaDatapointDate,
  SchemaDatapointEnum,
  SchemaDatapointNumber,
  SchemaDatapointString,
  SchemaSection,
  SchemaSimpleMultivalue,
  SchemaTableMultivalue,
  SchemaTuple,
} from '@rossum/api-client/schemas';
import { FieldsFormModel } from '../formModels';
import {
  UnknownRecord,
  ValueTypeAtStringPath,
} from '../transformations-utils/types';

export type AllKeys<T> = T extends unknown ? keyof T : never;

type CombineProps2<S extends UnknownRecord, T extends UnknownRecord> = {
  [K in keyof T | keyof S]:
    | (K extends keyof T ? T[K] : never)
    | (K extends keyof S ? S[K] : never);
};

type CombineProps<T extends Array<unknown>> = T extends [
  infer First extends UnknownRecord,
  infer Second extends UnknownRecord,
  ...infer Tail,
]
  ? CombineProps<[CombineProps2<First, Second>, ...Tail]>
  : T extends [infer First extends UnknownRecord]
    ? First
    : never;

// Represents an object that contains _all_ available properties of `SchemaItem` or `SchemaSection`
// export type AnySchemaItem = Required<Merge<SchemaSection | SchemaItem>>;
export type AnySchemaItem = CombineProps<
  [
    SchemaDatapointString,
    SchemaDatapointNumber,
    SchemaDatapointDate,
    SchemaDatapointEnum,
    SchemaDatapointButton,
    SchemaSimpleMultivalue,
    SchemaTableMultivalue,
    SchemaSection,
    SchemaTuple,
  ]
>;

type TypeAt<Path extends string> = ValueTypeAtStringPath<AnySchemaItem, Path>;

export type PropertyExtractor<Path extends string> = (
  fallback: TypeAt<Path>
) => (blueprint: FieldsFormModel) => TypeAt<Path>;

export type PropertyEvolver<Path extends string> = (
  blueprint: FieldsFormModel,
  inChildren?: boolean
) => (fallback: TypeAt<Path>) => TypeAt<Path> | undefined;

const isEmpty = (val: unknown) =>
  val === undefined || val === null || val === '';

const isNotNullish = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

// Strips "empty" (see above) values deeply
// TODO: How to better types
export const stripEmpties = <T>(input: T): T | undefined => {
  if (!R.isPlainObject(input)) {
    return input;
  }

  const recursivelyStrippedObject = R.mapValues(input, (value, key) => {
    if (key === 'options' || key === 'configuration') {
      return value;
    }

    if (R.isPlainObject(value)) {
      return stripEmpties(value);
    }

    if (R.isArray(value)) {
      return value.map(stripEmpties).filter(isNotNullish);
    }

    return value;
  });

  return R.omitBy(recursivelyStrippedObject, (value, key) => {
    if ((key === 'formula' || key === 'prompt') && value === '') {
      return false;
    }

    if (R.isPlainObject(value)) {
      return R.values(value).every(isEmpty);
    }

    return isEmpty(value);
  }) as T | undefined;
};
